import { useAtom } from 'jotai';
import { useEffect, useCallback } from 'react';
import { settingsAtom, settingsInitializedAtom } from '../atoms/settingsAtoms';
import { settingsApi } from '../services/api';

/**
 * Custom hook to fetch and manage application settings
 * Ensures settings are only fetched once across the application
 */
export const useSettings = () => {
  const [settings, setSettings] = useAtom(settingsAtom);
  const [initialized, setInitialized] = useAtom(settingsInitializedAtom);

  // Function to fetch settings from API
  const fetchSettings = useCallback(async () => {
    // Skip if already loading or loaded
    if (settings.isLoading || (settings.isLoaded && initialized)) {
      return settings.settings;
    }

    try {
      // Set loading state
      setSettings(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Fetch settings from API
      const response = await settingsApi.getSettings();
      
      // Update settings atom with fetched data
      setSettings({
        isLoaded: true,
        isLoading: false,
        error: null,
        settings: response.settings || {}
      });

      console.log(response.settings);
      
      // Mark as initialized
      setInitialized(true);
      
      return response.settings || {};
    } catch (error) {
      console.error('Error fetching settings:', error);
      
      // Update error state
      setSettings(prev => ({
        ...prev,
        isLoaded: true, // Mark as loaded even on error to prevent retries
        isLoading: false,
        error: error.message || 'Failed to load settings'
      }));
      
      return settings.settings;
    }
  }, []); // Remove dependencies to prevent re-creation

  // Fetch settings on first mount if not initialized
  useEffect(() => {
    // Only fetch settings once when the component mounts
    if (!initialized && !settings.isLoading && !settings.isLoaded) {
      fetchSettings();
    }
  }, []);  // Empty dependency array to ensure it only runs once

  // Function to get a specific setting with optional default value
  const getSetting = useCallback((key, defaultValue = '') => {
    return settings.settings[key] || defaultValue;
  }, [settings]);

  return {
    settings: settings.settings,
    isLoading: settings.isLoading,
    isLoaded: settings.isLoaded,
    error: settings.error,
    fetchSettings,
    getSetting
  };
};

export default useSettings;
